import { useFlag } from '@reflag/react-sdk';
import clsx from 'clsx';
import type { RefObject } from 'react';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import {
    type AnalyticsContextSpaceContent,
    logEvent,
    type RawEditAnalyticsEvent,
} from '@gitbook/app-analytics/client';
import type { NavigationScreenSpaceState } from '@gitbook/app-navigation';
import { createEditorValue, createSheetEditorValue, isDocumentEmpty } from '@gitbook/doc-core';
import type { HiveWorkingBranch, HiveWorkingDocument } from '@gitbook/hive-client';
import * as hiveCore from '@gitbook/hive-core';
import { hasPageCover, PageLayoutOptionsWidth } from '@gitbook/hive-core';
import {
    HiveWorkingDocumentProvider,
    useHiveBranch,
    useHiveClient,
    useHiveComputedDocument,
    useHiveReadOnly,
    useHiveSlateHistoryPlugin,
    useHiveWorkingDocument,
    useHiveWorkingDocumentValue,
} from '@gitbook/hive-react';
import type { Change, SelectionRange, Value } from '@gitbook/slate';
import type { EditorContainer, Plugin } from '@gitbook/slate-react';
import { Heading, ThemeContext } from '@gitbook/spine-react';
import type { List } from '@gitbook/util-immutable';
import { useBeforeUnload, useEventCallback } from '@gitbook/util-react-hooks';
import { getSuspense } from '@gitbook/util-suspense-cache';
import { assert, assertIsDefined, filterOutNullable } from '@gitbook/util-ts';

import {
    BlockCommentButton,
    type BlockConfiguration,
    BlockLayout,
    BlockLayoutProvider,
    BlockWidthWrapper,
    ContentEditor,
    FetchComments,
    focusEditor,
    PageLayoutProvider,
    PageWidthWrapper,
    useContentEditorIntegrationData,
    useEditorCopyPaste,
    useHideSplashscreen,
    usePageLayoutOptions,
    useSectionIDs,
} from '../../../../Components';
import { useNavigationState, useSetAnalyticsScreen } from '../../../../navigation';
import { useContentKitRemote, useSetHoveredCommentNode } from '../../../../state';
import { ConflictsPageBanner } from '../ConflictsPageBanner';
import { BlockCommentHalo } from '../comments';
import { DragSelection } from '../DragSelection';
import { PageCover } from '../PageCover';
import { PageEmptyState } from '../PageEmptyState';
import { PageHeader } from '../PageHeader';
import { PageOutline } from '../PageOutline';
import { PageSwitch } from '../PageSwitch';
import type { PageToolbarProps } from '../PageToolbar';
import { SpaceQuickstart } from '../quickstart/SpaceQuickstart';
import { ReadOnlyPageGroupSummary } from '../ReadOnlyPageGroupSummary';
import { SpaceQuickActions } from '../SpaceQuickActions';
import { useInitialScrollPage } from '../useInitialScrollPage';
import { usePageDocumentDiffValue } from '../usePageDocumentDiffValue';
import { SiteCustomizationProvider } from './SiteCustomizationProvider';
import { useAnalyticsTrackStartEdit } from './useAnalyticsTrackStartEdit';
import { useFocusPage } from './useFocusPage';

export type ScrollRef = React.RefObject<HTMLElement>;

export interface PageSheetViewProps {
    space: api.Space;
    revision: hiveCore.Revision;
    changeRequest: api.ChangeRequest | undefined;
    page: hiveCore.RevisionPageDocument;
    computedOrigin: hiveCore.RevisionPageComputed | undefined;
    readOnly: boolean;
    withComments: boolean;
    withToC: boolean;
    withDiff?: boolean;
    analyticsContext: AnalyticsContextSpaceContent;
    screen?: NavigationScreenSpaceState;
    scrollRef: ScrollRef;
    footer?: React.ReactNode;
    withOutline: boolean;
    toolbar?: React.FC<PageToolbarProps>;
    showToC?: boolean;
}

/**
 * View to render the inner content of a page sheet.
 */
export function PageSheetView(props: PageSheetViewProps) {
    const { page, computedOrigin, screen, scrollRef, changeRequest, readOnly } = props;
    const hiveBranch = useHiveBranch();
    const writeWithAIFeature = useFlag('WRITE_WITH_AI');

    /**
     * We attach `useInitialScrollPage` after React has finished suspending rendering the actual page content
     * so that it can scroll at the right position (top or hash).
     */
    useInitialScrollPage({ page, screen, scrollRef });
    useHideSplashscreen(0);

    const isFrozenContent =
        !!computedOrigin ||
        !hiveBranch ||
        (screen && screen.revision !== undefined) ||
        (screen?.changeRequest &&
            changeRequest &&
            [api.ChangeRequestStatus.Merged, api.ChangeRequestStatus.Archived].includes(
                changeRequest.status
            ));

    const pageSheet = (() => {
        if (page.computed) {
            assertIsDefined(computedOrigin);
            return (
                <PageSheetComputed
                    {...props}
                    computedSource={page.computed.source}
                    computedSeed={page.computed.seed}
                    readOnly
                />
            );
        }

        return isFrozenContent ? (
            <PageSheetFrozen {...props} />
        ) : (
            <HiveWorkingDocumentProvider
                containerType="pages"
                containerId={page.id}
                readOnly={readOnly}
            >
                <PageSheetLive {...props} withCompletions={writeWithAIFeature.isEnabled} />
            </HiveWorkingDocumentProvider>
        );
    })();

    return (
        <FetchComments
            spaceId={props.space.id}
            pageId={page.id}
            changeRequestId={changeRequest?.id}
            skip={!props.withComments}
        >
            {pageSheet}
        </FetchComments>
    );
}

/** Page content when rendering a computed page */
function PageSheetComputed(
    props: PageSheetViewProps & {
        computedSeed: string;
        computedSource: hiveCore.ComputedContentSourceDocument;
    }
) {
    const { space, computedSeed, computedSource } = props;
    const computedDocument = useHiveComputedDocument({
        contentId: space.id,
        computedSeed,
        computedSource,
    });
    const value = React.useMemo(() => createEditorValue(computedDocument), [computedDocument]);

    return <PageSheetViewWithValue {...props} hivePage={null} value={value} />;
}

/** Page content when rendering data from cold storage */
function PageSheetFrozen(props: PageSheetViewProps) {
    const { space, page } = props;
    const hiveClient = useHiveClient();
    const pageDocument = page.document
        ? getSuspense(hiveClient.store.getDocument(space.id, page.document))
        : undefined;

    const value = React.useMemo(() => {
        return pageDocument
            ? createEditorValue(pageDocument)
            : createSheetEditorValue(page.id, 'document');
    }, [pageDocument, page.id]);

    const documentValue = usePageDocumentDiffValue({ pageId: page.id, value });

    return <PageSheetViewWithValue {...props} hivePage={null} value={documentValue} readOnly />;
}

type PageSheetLiveProps = PageSheetViewProps & {
    withCompletions: boolean;
};

/** Page content when connected to the data sync (editable or not) */
function PageSheetLive(props: PageSheetLiveProps) {
    const { page, analyticsContext, readOnly } = props;

    const hiveBranch = useHiveBranch();
    assert(hiveBranch);

    const hivePage = useHiveWorkingDocument();
    assert(hivePage);

    const trackStartEdit = useAnalyticsTrackStartEdit({ analyticsContext, hivePage });
    const slateHistoryPlugin = useHiveSlateHistoryPlugin(hivePage);
    const editorPlugins = React.useMemo(() => [slateHistoryPlugin], [slateHistoryPlugin]);

    const hivePageReadOnly = useHiveReadOnly(hivePage);
    const [value, selections, onHiveChange] = useHiveWorkingDocumentValue(hivePage);
    const documentValue = usePageDocumentDiffValue({ pageId: page.id, value });

    const onChange = useEventCallback((change: Change) => {
        onHiveChange(change);

        if (change.value.document !== change.initialValue.document) {
            trackStartEdit();
        }
    });

    return (
        <>
            <HiveHooks hiveBranch={hiveBranch} hivePage={hivePage} />
            <PageSheetViewWithValue
                {...props}
                readOnly={readOnly || hivePageReadOnly}
                value={documentValue}
                onChange={onChange}
                selections={selections}
                editorPlugins={editorPlugins}
                hivePage={hivePage}
            />
        </>
    );
}

const HiveHooks = React.memo(function HiveHooks(props: {
    hivePage: HiveWorkingDocument;
    hiveBranch: HiveWorkingBranch;
}) {
    const { hivePage, hiveBranch } = props;

    useBeforeUnload(() => {
        // Check if the data sync is still open or has been closed already.
        // It can for instance have been closed if the user signed out, in which case we cannot
        // flush anything to the server.
        if (hivePage.dataSync.isClosed) {
            return null;
        }

        // Block the user from reloading the page if they still have pending updates.
        const hasUnsyncedUpdates = hivePage.dataSync.hasUnsyncedUpdates();

        // Trigger a flush so that the next time they refresh there's a good chance the changes are flushed.
        void hivePage.dataSync.waitForSync();

        if (hasUnsyncedUpdates) {
            return 'You have unsaved changes';
        }

        return null;
    });

    React.useEffect(() => {
        return () => {
            // When switching between pages / leaving the page; we want to send all the batched updates
            hivePage.dataSync.flushBatchUpdates();
        };
    }, [hivePage]);

    React.useEffect(() => {
        // When the page is mounted, we dispatch to set the presence
        if (hivePage.containerType === 'pages') {
            hiveBranch.openPage(hivePage.containerId);
        }
    }, [hiveBranch, hivePage]);
    return null;
});

const noop = () => {};

/** Content initialized with the editor value */
function PageSheetViewWithValue(
    props: PageSheetViewProps & {
        /** Page document value */
        value: Value;

        hivePage: HiveWorkingDocument | null;

        /** Callback to apply updates to the page document */
        onChange?: (change: Change) => void;

        /** The page document current selections */
        selections?: List<SelectionRange>;

        /** Plugins to pass to the slate editor */
        editorPlugins?: Plugin[];

        withCompletions?: boolean;
    }
) {
    const {
        space,
        revision,
        page,
        readOnly = true,
        value,
        onChange = noop,
        selections,
        withComments,
        withCompletions,
        analyticsContext,
        withOutline = false,
        toolbar: PageToolbar,
        footer,
        withDiff,
        scrollRef,
        editorPlugins,
        computedOrigin,
    } = props;

    const navigationState = useNavigationState();
    const integrationBlocksRemote = useContentKitRemote();

    const editorRef = React.useRef<EditorContainer>(null);

    const sectionIDs = useSectionIDs(value.document);

    // When user press Enter in description, we focus the body
    const onSubmitDescription = React.useCallback(() => {
        focusEditor(editorRef.current, { collapseToStart: true });
    }, []);

    const logEditorEvent = React.useCallback(
        (event) => {
            logEvent({
                ...analyticsContext,
                ...event,
            });
        },
        [analyticsContext]
    );

    const copyPaste = useEditorCopyPaste({ space: space.id, organization: space.organization });

    const conflictType = revision.conflicts?.pages[page.id];

    const emptyDocument = isDocumentEmpty(value.document);
    const withBlankslate = emptyDocument && !readOnly;

    const RightSideGutterComponent = withComments ? BlockCommentButton : undefined;

    const setHoveredCommentNode = useSetHoveredCommentNode();
    const onBlockHover = React.useCallback(
        (node: string | null) => {
            if (node) {
                setHoveredCommentNode.highlight(node, { highlighting: false, postButton: true });
            } else {
                setHoveredCommentNode.highlight(null);
            }
        },
        [setHoveredCommentNode]
    );

    const blockConfiguration: BlockConfiguration = React.useMemo(() => {
        return {
            withImages: true,
            withAPIs: true,
            withCompletions,
            withDrawings: true,
            withFiles: true,
            withReusableContent: true,
        };
    }, [withCompletions]);

    const pageOptions = usePageLayoutOptions(page);

    const pageTitleRef = React.useRef<HTMLTextAreaElement>(null);

    // Focus the page editor when moving between pages
    useFocusPage({
        pageTitleRef,
        page,
        editorRef,
        isReadOnly: readOnly,
    });

    const shouldShowCover = Boolean(pageOptions.cover) && hasPageCover(page);
    const { coverSize } = pageOptions;

    const integrationBlocksContext = useIntegrationBlocksContext({
        editable: !readOnly,
        spaceId: space.id,
    });

    const integrationData = useContentEditorIntegrationData({
        spaceId: space.id,
        organizationId: space.organization,
    });

    const selectableAreaRef = React.useRef<HTMLDivElement>(null);

    // Track the empty state of the space
    const isEmpty =
        !computedOrigin &&
        emptyDocument &&
        Object.keys(revision.pages).length <= 1 &&
        page.title === hiveCore.DEFAULT_PAGE_TITLE;

    useSetAnalyticsScreen(
        isEmpty ? { screen: 'space_empty_state', editable: !readOnly, ...analyticsContext } : null,
        [isEmpty, readOnly]
    );

    const isOnChangeRequest =
        navigationState.screen.type === 'space' && navigationState.screen.changeRequest;

    if (isEmpty && readOnly && !isOnChangeRequest) {
        return <PageEmptyState space={space} screen={navigationState.screen} />;
    }

    return (
        <PageLayoutProvider width={pageOptions.width}>
            <BlockLayoutProvider className="min-h-full">
                {/* Page Cover - Full */}
                {shouldShowCover && coverSize === hiveCore.PageLayoutOptionsCoverSize.Full ? (
                    <PageCover
                        page={page}
                        readOnly={readOnly}
                        analyticsContext={analyticsContext}
                    />
                ) : null}

                {/* Page content + outline */}
                <div ref={selectableAreaRef} className="@container/page relative size-full">
                    <div
                        className={clsx(
                            'grid size-full items-start lg:px-22',
                            withDiff
                                ? 'max-lg:px-[72px]'
                                : 'max-md:px-7 md:px-12 page-has-comments:md:px-22',
                            // Single column below `@container/page` width
                            'grid-cols-1',
                            // Center the grid
                            '@[75rem]/page:justify-center @[75rem]/page:gap-x-10',
                            // Side columns shrink first; content remains above `--block-wrapper-max-width` as much as possible
                            '@[75rem]/page:grid-cols-[minmax(var(--page-outline-min-width),_var(--page-outline-max-width))_minmax(var(--block-wrapper-max-width),_1fr)_minmax(var(--page-outline-min-width),_var(--page-outline-max-width))]'
                        )}
                    >
                        {/* Page content */}
                        <PageWidthWrapper
                            page={page}
                            className={clsx(
                                // Content in middle column when the grid is active
                                '@[75rem]/page:col-start-2 min-w-0',
                                // Center the page content
                                '@[75rem]/page:mx-auto'
                            )}
                        >
                            {/* Page content */}
                            <div className="h-full">
                                {/* Page Cover - Hero */}
                                {shouldShowCover &&
                                coverSize === hiveCore.PageLayoutOptionsCoverSize.Hero ? (
                                    <BlockWidthWrapper className="flex">
                                        <div className="mt-4 flex flex-1">
                                            <PageCover
                                                page={page}
                                                readOnly={readOnly}
                                                analyticsContext={analyticsContext}
                                            />
                                        </div>
                                    </BlockWidthWrapper>
                                ) : null}

                                <main className="relative flex h-full flex-1 flex-col justify-between pb-10">
                                    <div>
                                        {/* Header */}
                                        <BlockWidthWrapper className="flex">
                                            <div
                                                data-drag-selection-ignore
                                                className={clsx(
                                                    'flex flex-1',
                                                    pageOptions.width ===
                                                        PageLayoutOptionsWidth.Wide
                                                        ? 'justify-start'
                                                        : 'justify-center'
                                                )}
                                            >
                                                <PageHeader
                                                    pageTitleRef={pageTitleRef}
                                                    space={space}
                                                    page={page}
                                                    readOnly={readOnly}
                                                    onSubmitDescription={onSubmitDescription}
                                                    analyticsContext={analyticsContext}
                                                    withDiff={withDiff}
                                                    toolbar={PageToolbar}
                                                />
                                            </div>
                                        </BlockWidthWrapper>

                                        {conflictType !== undefined ? (
                                            <div data-drag-selection-ignore>
                                                <BlockLayout renderMode="marginless-centered">
                                                    <ConflictsPageBanner
                                                        page={page}
                                                        document={value.document}
                                                        conflictType={conflictType}
                                                    />
                                                </BlockLayout>
                                            </div>
                                        ) : null}

                                        {/* Content */}
                                        {readOnly && emptyDocument && page.pages.length ? (
                                            // if we don't have any content in the page and there are some sub-pages,
                                            // then we are going to show a gallery of the sub-pages
                                            <BlockLayout renderMode="centered">
                                                <ReadOnlyPageGroupSummary
                                                    subPages={page.pages
                                                        .map((pageKey) => {
                                                            const page = revision.pages[pageKey];
                                                            return hiveCore.isRevisionPageParent(
                                                                page
                                                            ) && !!page.hidden
                                                                ? null
                                                                : page;
                                                        })
                                                        .filter(filterOutNullable)}
                                                    revision={revision}
                                                />
                                            </BlockLayout>
                                        ) : (
                                            <>
                                                <SiteCustomizationProvider space={space}>
                                                    {/* Document */}
                                                    <ContentEditor
                                                        ref={editorRef}
                                                        value={value}
                                                        selections={selections}
                                                        onChange={onChange}
                                                        readOnly={readOnly}
                                                        logAnalyticsEvent={logEditorEvent}
                                                        blocks={blockConfiguration}
                                                        placeholder="Enter your content here…"
                                                        sectionIDs={sectionIDs}
                                                        selectedSection={
                                                            navigationState.screen.hash || null
                                                        }
                                                        rightGutterComponent={
                                                            RightSideGutterComponent
                                                        }
                                                        blockHighlightComponent={BlockCommentHalo}
                                                        integrationBlocksContext={
                                                            integrationBlocksContext
                                                        }
                                                        integrationBlocksRemote={
                                                            integrationBlocksRemote
                                                        }
                                                        integrationData={integrationData}
                                                        onBlockHover={onBlockHover}
                                                        plugins={editorPlugins}
                                                        copyPaste={copyPaste}
                                                        /* Bring selected/hovered void frame nodes forward to prevent frame being hidden */
                                                        childrenClassName="has-[.void-selection-frame]:relative has-[.void-selection-frame[data-frame-selected]]:z-[1] has-[.void-selection-frame:hover]:z-[2]"
                                                    />
                                                </SiteCustomizationProvider>
                                                {withBlankslate ? (
                                                    <BlankSlate
                                                        editorRef={editorRef}
                                                        logAnalyticsEvent={logEditorEvent}
                                                        space={space}
                                                        isSpaceEmpty={isEmpty}
                                                        pageId={page.id}
                                                    />
                                                ) : null}
                                            </>
                                        )}
                                    </div>

                                    {/* Footer */}
                                    {withBlankslate ? null : (
                                        <PageFooter
                                            analyticsContext={analyticsContext}
                                            pageId={page.id}
                                            pageOptions={pageOptions}
                                            revision={revision}
                                            space={space}
                                            footer={footer}
                                        />
                                    )}
                                </main>
                            </div>
                        </PageWidthWrapper>

                        {/* Page outline */}
                        {withOutline ? (
                            <PageOutline
                                document={value.document}
                                scrollRef={scrollRef}
                                sectionIDs={sectionIDs}
                                className={clsx(
                                    // Hidden below breakpoint
                                    'hidden',
                                    // Right column above `@container/page` width
                                    '@[75rem]/page:col-start-3 @[75rem]/page:block',
                                    // Padding and fade-in behavior
                                    'page-has-outline:@[75rem]/page:pr-2.5 page-has-outline:@[75rem]/page:pl-6',
                                    '@[75rem]/page:opacity-0 page-has-outline:@[75rem]/page:opacity-100',
                                    // keep tracks from expanding due to min-content
                                    'min-w-0'
                                )}
                            />
                        ) : null}
                    </div>
                </div>

                <DragSelection
                    disabled={readOnly}
                    editorRef={editorRef}
                    selectableAreaRef={selectableAreaRef}
                    scrollRef={scrollRef}
                    logAnalyticsEvent={logEditorEvent}
                />
            </BlockLayoutProvider>
        </PageLayoutProvider>
    );
}

function useIntegrationBlocksContext(props: { editable: boolean; spaceId: string }) {
    const { editable, spaceId } = props;
    const theme = React.useContext(ThemeContext);
    return React.useMemo<api.ContentKitContextDocument>(
        () => ({ type: 'document', editable, spaceId, theme }),
        [editable, theme, spaceId]
    );
}

const PageFooter = React.memo(function PageFooter(
    props: Pick<PageSheetLiveProps, 'analyticsContext' | 'space' | 'revision' | 'footer'> & {
        pageOptions: hiveCore.PageLayoutOptions;
        pageId: string;
    }
) {
    const { pageOptions, analyticsContext, space, revision, pageId, footer } = props;
    return (
        <BlockWidthWrapper
            className={clsx(
                'mt-6 flex w-full',
                pageOptions.width === 'wide'
                    ? 'justify-start'
                    : 'max-w-[--block-wrapper-max-width] page-block-openapi:max-w-full justify-center'
            )}
        >
            <div data-drag-selection-ignore className="flex flex-1">
                <div className="flex-1">
                    <div>
                        {/* Page Switch (Previous/Next) */}
                        {pageOptions.pagination ? (
                            <PageSwitch
                                analyticsContext={analyticsContext}
                                space={space}
                                revision={revision}
                                pageId={pageId}
                            />
                        ) : null}

                        {/* Footer */}
                        {footer && pageOptions.metadata ? footer : null}
                    </div>
                </div>
            </div>
        </BlockWidthWrapper>
    );
});

/**
 * Blank slate for the page editor.
 * Displays quick actions and quickstart when the editor is focused.
 */
function BlankSlate(props: {
    editorRef: RefObject<EditorContainer>;
    logAnalyticsEvent: (event: RawEditAnalyticsEvent) => void;
    space: api.Space;
    isSpaceEmpty: boolean;
    pageId: string;
}) {
    const { editorRef, logAnalyticsEvent, isSpaceEmpty, pageId, space } = props;

    return (
        <>
            <BlockLayout
                renderMode="centered"
                className="mx-auto w-full max-w-[--block-wrapper-max-width]"
            >
                <SpaceQuickActions editorRef={editorRef} logAnalyticsEvent={logAnalyticsEvent} />
            </BlockLayout>

            <div className="absolute bottom-0 w-full">
                <BlockLayout
                    renderMode="centered"
                    className="mx-auto w-full max-w-[--block-wrapper-max-width]"
                >
                    <div className="flex flex-col gap-2">
                        <Heading level={3} className="text-muted text-ui-heading-group">
                            Quickstart
                        </Heading>
                        <SpaceQuickstart
                            editorRef={editorRef}
                            space={space}
                            logAnalyticsEvent={logAnalyticsEvent}
                            className="pb-4" // Needed to show the shadows
                            isSpaceEmpty={isSpaceEmpty}
                            pageId={pageId}
                        />
                    </div>
                </BlockLayout>
            </div>
        </>
    );
}
